import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';

const currency = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const riskClassMap = {
  healthy: 'badge-success',
  warning: 'badge-warning',
  risk: 'badge-danger',
};

function formatDate(value) {
  return value ? new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString('pt-BR') : 'Nao informado';
}

function formatDateTime(value) {
  return value ? new Date(value).toLocaleString('pt-BR') : 'Nao informado';
}

const PersonalFinanceHomePage = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/personal-finance/overview');
      setData(response.data || null);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar overview do modulo pessoal');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const connectivity = data?.connectivity || { summary: {}, urgent_items: [] };
  const notifications = data?.notifications || { summary: {}, items: [] };

  const cards = [
    {
      label: 'Posso gastar hoje',
      value: currency.format(Number(data?.daily_clarity?.safe_to_spend_today || 0)),
      helper: 'Valor livre sem pressionar o buffer futuro',
    },
    {
      label: 'Menor saldo 30d',
      value: currency.format(Number(data?.daily_clarity?.lowest_projected_balance || 0)),
      helper: data?.daily_clarity?.lowest_projected_balance_date
        ? `Ponto de tensao em ${formatDate(data.daily_clarity.lowest_projected_balance_date)}`
        : 'Sem data critica no horizonte',
    },
    {
      label: 'Conexoes em alerta',
      value: String(
        Number(connectivity.summary?.action_required_connections || 0) + Number(connectivity.summary?.attention_connections || 0)
      ),
      helper: Number(connectivity.summary?.action_required_connections || 0) > 0
        ? `${connectivity.summary?.action_required_connections || 0} exigem acao imediata`
        : 'Sem reautorizacoes ou stale syncs criticos',
    },
    {
      label: 'Patrimonio liquido',
      value: currency.format(Number(data?.wealth?.summary?.net_worth_total || 0)),
      helper: 'Ativos e passivos consolidados no modulo pessoal',
    },
    {
      label: 'Alertas nao lidos',
      value: String(notifications.summary?.unread_count || 0),
      helper: Number(notifications.summary?.high_open_count || 0) > 0
        ? `${notifications.summary?.high_open_count || 0} em severidade alta`
        : 'Inbox operacional sob controle',
    },
  ];

  const checklist = [
    {
      done: data?.onboarding?.household_ready === true,
      title: 'Household inicializado',
      helper: 'Workspace pessoal criado no bounded context novo.',
    },
    {
      done: data?.onboarding?.income_configured === true,
      title: 'Renda principal configurada',
      helper: 'Define base para forecast e safe-to-spend.',
    },
    {
      done: data?.onboarding?.safety_buffer_configured === true,
      title: 'Buffer de seguranca configurado',
      helper: 'Evita previsoes otimistas demais.',
    },
    {
      done: data?.onboarding?.connections_ready === true,
      title: 'Conexao pronta',
      helper: 'Conecta o motor de dados sem contaminar o financeiro corporativo.',
    },
    {
      done: Number(connectivity.summary?.action_required_connections || 0) === 0,
      title: 'Saude de conexoes sob controle',
      helper: Number(connectivity.summary?.action_required_connections || 0) > 0
        ? `${connectivity.summary?.action_required_connections || 0} conexoes pedem reautorizacao ou sync imediato.`
        : 'Consentimentos e syncs estao dentro da janela operacional esperada.',
    },
    {
      done: Number(data?.stats?.active_commitments || 0) > 0,
      title: 'Agenda de compromissos viva',
      helper: 'Sem compromissos mapeados, o forecast fica superficial.',
    },
    {
      done: Number(data?.wealth?.summary?.total_assets || 0) > 0 || Number(data?.wealth?.summary?.total_liabilities || 0) > 0,
      title: 'Camada de patrimonio ativa',
      helper: 'O modulo agora consegue ler ativos, passivos e patrimonio liquido.',
    },
    {
      done: Array.isArray(data?.insights) && data.insights.length > 0,
      title: 'Camada de insights ativa',
      helper: 'O modulo agora detecta recorrencias suspeitas e pressao de caixa/cartao.',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
        {cards.map((item) => (
          <div key={item.label} className="card">
            <div className="card-body space-y-3">
              <p className="text-sm text-slate-500">{item.label}</p>
              <p className="text-2xl font-semibold text-slate-900">{loading ? '...' : item.value}</p>
              <p className="text-sm text-slate-500">{item.helper}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.25fr_0.9fr]">
        <div className="card">
          <div className="card-body space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">Clareza diaria</p>
                <h2 className="text-xl font-semibold text-slate-900">Estado atual do workspace pessoal</h2>
              </div>
              <button className="btn-secondary" onClick={loadData} disabled={loading}>Atualizar</button>
            </div>

            <div className="rounded-3xl border border-emerald-100 bg-emerald-50/70 p-5">
              <p className="text-sm text-emerald-900">
                Household: <span className="font-semibold">{data?.household?.display_name || 'Carregando...'}</span>
              </p>
              <p className="mt-2 text-sm text-emerald-800">
                Slice 09 ativo: o modulo agora fecha o loop entre health center, jobs operacionais e inbox de alertas sem sair do contexto pessoal.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-emerald-800">
                <span>Metas ativas: <span className="font-semibold">{loading ? '...' : String(data?.stats?.active_goals || 0)}</span></span>
                <span>Sinais no radar: <span className="font-semibold">{loading ? '...' : String(data?.daily_clarity?.upcoming_items_count || 0)}</span></span>
                <span>Patrimonio: <span className="font-semibold">{loading ? '...' : currency.format(Number(data?.wealth?.summary?.net_worth_total || 0))}</span></span>
                <span>Conexoes em acao: <span className="font-semibold">{loading ? '...' : String((connectivity.summary?.action_required_connections || 0) + (connectivity.summary?.attention_connections || 0))}</span></span>
                <span>Alertas abertos: <span className="font-semibold">{loading ? '...' : String(notifications.summary?.open_count || 0)}</span></span>
                <span className={riskClassMap[data?.daily_clarity?.risk_status] || 'badge-info'}>
                  {data?.daily_clarity?.risk_status || 'unknown'}
                </span>
              </div>
            </div>

            {(Number(connectivity.summary?.action_required_connections || 0) > 0 || Number(connectivity.summary?.attention_connections || 0) > 0) && (
              <div className="rounded-3xl border border-rose-200 bg-rose-50/80 p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-rose-700">Health center</p>
                    <p className="mt-2 text-lg font-semibold text-slate-900">
                      {Number(connectivity.summary?.action_required_connections || 0) > 0
                        ? `${connectivity.summary?.action_required_connections || 0} conexoes exigem acao imediata`
                        : `${connectivity.summary?.attention_connections || 0} conexoes merecem monitoramento`}
                    </p>
                    <p className="mt-2 text-sm text-slate-600">
                      Expiracao de consentimento, stale sync e backoff passam a entrar no radar diario do modulo.
                    </p>
                  </div>
                  <Link to="/pessoal/conexoes" className="btn-secondary">
                    Abrir conexoes
                  </Link>
                </div>
                {Array.isArray(connectivity.urgent_items) && connectivity.urgent_items[0] && (
                  <div className="mt-4 rounded-2xl border border-rose-200 bg-white p-4">
                    <p className="font-semibold text-slate-900">{connectivity.urgent_items[0].display_name}</p>
                    <p className="mt-1 text-sm text-slate-500">{connectivity.urgent_items[0].primary_issue}</p>
                    <p className="mt-2 text-sm text-rose-700">{connectivity.urgent_items[0].recommended_action}</p>
                    {connectivity.urgent_items[0].backoff_until_at && (
                      <p className="mt-2 text-xs text-slate-400">Backoff ate {formatDateTime(connectivity.urgent_items[0].backoff_until_at)}</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {(notifications.items || [])[0] && (
              <div className="rounded-3xl border border-sky-200 bg-sky-50/80 p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-700">Inbox prioritario</p>
                    <p className="mt-2 text-lg font-semibold text-slate-900">{notifications.items[0].title}</p>
                    <p className="mt-2 text-sm text-slate-600">{notifications.items[0].body}</p>
                    <p className="mt-3 text-sm text-sky-800">
                      {notifications.summary?.unread_count || 0} nao lidos / {notifications.summary?.open_count || 0} abertos no modulo.
                    </p>
                  </div>
                  <Link to={notifications.items[0].cta_route || '/pessoal/alertas'} className="btn-secondary">
                    {notifications.items[0].cta_label || 'Abrir alertas'}
                  </Link>
                </div>
              </div>
            )}

            {Array.isArray(data?.insights) && data.insights[0] && (
              <div className="rounded-3xl border border-amber-200 bg-amber-50/80 p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-700">Insight prioritario</p>
                <p className="mt-2 text-lg font-semibold text-slate-900">{data.insights[0].title}</p>
                <p className="mt-2 text-sm text-slate-600">{data.insights[0].description}</p>
                <p className="mt-3 text-sm text-amber-800">{data.insights[0].recommended_action}</p>
              </div>
            )}

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Link to="/pessoal/caixa" className="rounded-2xl border border-slate-200 bg-white p-4 transition hover:border-slate-300">
                <p className="text-sm font-semibold text-slate-900">Abrir caixa</p>
                <p className="mt-1 text-sm text-slate-500">Ver safe-to-spend, timeline diaria e compromissos do forecast.</p>
              </Link>
              <Link to="/pessoal/insights" className="rounded-2xl border border-slate-200 bg-white p-4 transition hover:border-slate-300">
                <p className="text-sm font-semibold text-slate-900">Abrir insights</p>
                <p className="mt-1 text-sm text-slate-500">Ver desperdicios detectados, risco de fatura e base fixa pressionada.</p>
              </Link>
              <Link to="/pessoal/alertas" className="rounded-2xl border border-slate-200 bg-white p-4 transition hover:border-slate-300">
                <p className="text-sm font-semibold text-slate-900">Abrir alertas</p>
                <p className="mt-1 text-sm text-slate-500">Tratar o inbox operacional com CTA para caixa, conexoes, cartoes e revisao.</p>
              </Link>
              <Link to="/pessoal/patrimonio" className="rounded-2xl border border-slate-200 bg-white p-4 transition hover:border-slate-300">
                <p className="text-sm font-semibold text-slate-900">Abrir patrimonio</p>
                <p className="mt-1 text-sm text-slate-500">Consolidar ativos, passivos e historico de patrimonio liquido.</p>
              </Link>
              <Link to="/pessoal/revisao-semanal" className="rounded-2xl border border-slate-200 bg-white p-4 transition hover:border-slate-300">
                <p className="text-sm font-semibold text-slate-900">Revisao semanal</p>
                <p className="mt-1 text-sm text-slate-500">Encerrar a semana com checklist, radar curto prazo e acoes concretas.</p>
              </Link>
              <Link to="/pessoal/contas" className="rounded-2xl border border-slate-200 bg-white p-4 transition hover:border-slate-300">
                <p className="text-sm font-semibold text-slate-900">Operar contas</p>
                <p className="mt-1 text-sm text-slate-500">Explorar saldos, fluxo recente e adicionar transacoes mockadas.</p>
              </Link>
              <Link to="/pessoal/cartoes" className="rounded-2xl border border-slate-200 bg-white p-4 transition hover:border-slate-300">
                <p className="text-sm font-semibold text-slate-900">Operar cartoes</p>
                <p className="mt-1 text-sm text-slate-500">Ver limite, faturas derivadas e simular compras parceladas ou a vista.</p>
              </Link>
              <Link to="/pessoal/conexoes" className="rounded-2xl border border-slate-200 bg-white p-4 transition hover:border-slate-300">
                <p className="text-sm font-semibold text-slate-900">Saude de conexoes</p>
                <p className="mt-1 text-sm text-slate-500">Monitorar consentimentos, stale sync, reautorizacao e backoff operacional.</p>
              </Link>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body space-y-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Onboarding core</p>
              <h2 className="text-xl font-semibold text-slate-900">Checklist do modulo</h2>
            </div>
            <div className="space-y-3">
              {checklist.map((item) => (
                <div key={item.title} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-medium text-slate-900">{item.title}</p>
                    <span className={item.done ? 'badge-success' : 'badge-warning'}>{item.done ? 'Pronto' : 'Pendente'}</span>
                  </div>
                  <p className="mt-2 text-sm text-slate-500">{item.helper}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalFinanceHomePage;
